import { useMemo, useState } from 'react';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import { useDataService } from '../../services/ApiDataService';
import {
    NAME_FIELD,
    TAGS_FIELD,
    TEST_STATUS_FIELD,
    TEST_STATUS_RUNNING,
} from '../pages-admin/service-tests/ServiceTestAdminConstants';

export const useStyles = makeStyles((theme) => ({
    tagOverloadsAgentWarning: {
        width: '94%',
        padding: '4px',
        color: theme.palette.error.main,
    },
}));

function getTestDelta(originalTests, newTests) {
    const delta = [];

    if (!_.isEmpty(originalTests) && !_.isEmpty(newTests)) {
        originalTests.forEach((originalTest) => {
            const matchedTest = newTests.find((newTest) => {
                return originalTest._id === newTest._id;
            });
            if (matchedTest) {
                if (originalTest.runningOnGlobalnPoint !== matchedTest.runningOnGlobalnPoint) {
                    if (!matchedTest.runningOnGlobalnPoint) {
                        delta.push(matchedTest.name);
                    }
                }
            }
        });
    }

    return delta;
}

/**
 * Check if there a delta for agent
 * @param {React.PropsWithoutRef<NgpTagsFieldProps>} fieldProps
 * @returns
 */
function agentWithDelta(runningTestsDelta, agent) {
    const agentId = _.get(agent, 'agent._id', '');
    return (
        agentId &&
        !_.isEmpty(runningTestsDelta) &&
        _.has(runningTestsDelta, agentId) &&
        runningTestsDelta[agentId].length > 0
    );
}

/**
 * Check if there is only one agent and if there is a delta for that agent
 * @param {React.PropsWithoutRef<NgpTagsFieldProps>} fieldProps
 * @returns
 */
function singleAgentWithDelta(runningTestsDelta, agentTestDataList) {
    return agentTestDataList.length === 1 && agentWithDelta(runningTestsDelta, _.first(agentTestDataList));
}

/**
 *
 * @param {React.PropsWithoutRef<NgpTagsFieldProps>} fieldProps
 * @returns
 */
export default function NgpTagWarningField(fieldProps) {
    const classes = useStyles();
    const dataService = useDataService();
    const { watch } = useFormContext();
    const { fieldName, agentTestDataList } = fieldProps;
    const tagsValue = watch(fieldName);
    const [runningTestsDelta, setRunningTestsDelta] = useState([]);
    const [loading, setLoading] = useState(false);

    useMemo(() => {
        setLoading(true);
        const promiseList = [];

        _.forEach(agentTestDataList, (agentTestData) => {
            // Only need to run this query on addition of tags since removal will never disable tests
            if (
                _.get(agentTestData, 'agent._id') &&
                !_.isEmpty(agentTestData.tests) &&
                !_.isEmpty(_.difference(tagsValue, agentTestData.agent.tags))
            ) {
                const url = `/ipm/v1/admin/agents/${agentTestData.agent._id}/testsSearchable`;
                const params = {
                    sort: NAME_FIELD,
                    searchFields: [NAME_FIELD, TAGS_FIELD],
                    tags: _.uniq([...tagsValue, ...agentTestData.agent.tags]) || [],
                };
                promiseList.push(
                    dataService.get(url, params).then((tests) => {
                        return {
                            tests: getTestDelta(
                                agentTestData.tests,
                                _.filter(tests, (test) => test[TEST_STATUS_FIELD] === TEST_STATUS_RUNNING)
                            ),
                            _id: agentTestData.agent._id,
                        };
                    })
                );
            }
        });

        Promise.all(promiseList).then((updatedAgentTestData) => {
            setRunningTestsDelta(_.mapValues(_.keyBy(updatedAgentTestData, '_id'), 'tests'));
            setLoading(false);
        });
    }, [tagsValue, agentTestDataList, dataService]);

    if (loading) {
        return <div style={{ padding: '4px' }}>Checking nPoint test availability...</div>;
    }

    if (singleAgentWithDelta(runningTestsDelta, agentTestDataList)) {
        const agentId = _.get(_.first(agentTestDataList), 'agent._id');
        const deltaTests = runningTestsDelta[agentId];
        return (
            <div className={classes.tagOverloadsAgentWarning}>
                Change will exceed maximum test limit of 25, and {deltaTests?.length} tests will stop running:{' '}
                {deltaTests.join(', ')}
            </div>
        );
    }

    if (runningTestsDelta && _.keys(runningTestsDelta).length > 0 && agentTestDataList.length > 1) {
        let deltaTests = [];
        _.forOwn(runningTestsDelta, (testList) => {
            deltaTests = _.uniq([...deltaTests, ...testList]);
        });
        return (
            <div className={classes.tagOverloadsAgentWarning}>
                Change will exceed maximum test limit of 25, and {deltaTests.length} tests will stop running on{' '}
                {_.keys(runningTestsDelta).length} global nPoints.
            </div>
        );
    }

    return null;
}
